/** @format */

import {
  ChangeDetectionStrategy,
  Component,
  Injector,
  OnInit,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import {
  FavoriteProductState,
  FavoritesActionDeterminationData,
} from '@shared/favorites/data-access/interfaces';
import {
  FavoritesActionResult,
  FavoritesIconEnum,
  FavoritesLocationEnum,
} from '@shared/favorites/data-access/enums';
import {
  HomefieldIconsLibraryService,
  homefieldIconReactionLoveFill,
  homefieldIconReactionLoveOutline,
} from '@dsg-tech/homefield-angular-icons';
import { MyAccountAnalyticsService, SnackbarService, Store } from '@shared/data-access/services';
import { catchError, filter, map, of, switchMap } from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { ActivatedRoute } from '@angular/router';
import { DmnAdService } from '@shared/data-access/product-recommendations';
import { MyAccountErrorLogKeys } from '@shared/data-access/enums';
import { BeaconQueryParameters, ProductStyle } from '@shared/data-access/interfaces';
import { WishListService } from '@shared/favorites/data-access/services';
import { HmfButtonComponent } from '@dsg-tech/homefield-angular';

type FavoritesActionState = {
  result: FavoritesActionResult;
  productId: string | undefined;
  listName: string | null;
  ecode: string | null;
  resolvedSku: string | null;
  location: FavoritesLocationEnum;
};

@Component({
  selector: 'shared-favorites-components-auth-icon',
  templateUrl: './favorites-icon.component.html',
  styleUrls: ['./favorites-icon.component.scss'],
  imports: [HmfButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FavoritesIconComponent implements OnInit {
  private readonly injector = inject(Injector);
  private readonly wishlistService = inject(WishListService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly analyticsService = inject(MyAccountAnalyticsService);
  private readonly store = inject(Store);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly dmnAdService = inject(DmnAdService);
  private readonly homefieldIconsLibraryService = inject(HomefieldIconsLibraryService);

  readonly ecode = input<string | null>(null);
  readonly resolvedSku = input<string | null>(null);
  readonly location = input<FavoritesLocationEnum>(FavoritesLocationEnum.FAVORITES);
  readonly bundleProduct = input<ProductStyle | null>(null);
  readonly isAuthenticated = input<boolean>(false);
  readonly onWishlistBeacon = input<string>('');
  readonly favoritedECodes = input<string[]>([]);
  readonly disabled = model<boolean>(false);

  readonly product = input<FavoriteProductState | null>(null);

  readonly listName = input<string | null>(null);

  readonly listIndex = input<number | null>(null);

  readonly deleteProductEmit = output<string>();
  private readonly favoritesActionRequestState = signal<FavoritesActionDeterminationData | null>(
    null,
  );

  private readonly savedProductsFromStore = toSignal(
    this.store.selectDistinctDefinedKey('SAVEDPRODUCTS').pipe(
      catchError((err) => {
        this.analyticsService.generateLogError(
          MyAccountErrorLogKeys.FAVORITES,
          'Failed to get list from store',
          err,
        );
        return of(new Map<string, string[]>());
      }),
    ),
    {
      initialValue: new Map<string, string[]>(),
    },
  );

  readonly productsInStore = signal<Map<string, string[]>>(new Map());

  readonly favoritesButtonAriaLabel = computed(() => {
    if (!this.isAuthenticated()) {
      return 'Sign in to add to a list';
    }

    if (this.productIsFavorited()) {
      return 'Remove from a list';
    }

    return 'Add to a list';
  });

  private readonly favoritesActionState = toSignal(
    toObservable(this.favoritesActionRequestState).pipe(
      filter((actionRequest): actionRequest is FavoritesActionDeterminationData =>
        Boolean(actionRequest),
      ),
      switchMap((actionRequest) =>
        this.wishlistService.determineFavoritesAction(actionRequest).pipe(
          map((result) => ({
            result,
            productId: this.product()?.id,
            listName: this.listName(),
            ecode: this.ecode(),
            resolvedSku: this.resolvedSku(),
            location: this.location(),
          })),
          catchError((error) => {
            this.analyticsService.generateLogError(
              MyAccountErrorLogKeys.FAVORITES,
              'Failed to determine favorites action',
              error,
            );
            return of(null);
          }),
        ),
      ),
    ),
    { initialValue: null as FavoritesActionState | null },
  );

  readonly productIsFavorited = computed(() => {
    if (this.location() === this.favoritesLocationEnum.FAVORITES) {
      return true;
    }

    const ecode = this.ecode();
    const skuOrEcode = this.resolvedSku() ? this.resolvedSku() : ecode;
    if (skuOrEcode === null) {
      return false;
    }
    // If we're in FBT location, use externally provided favoritedECodes on initial load state
    // Use subsequent updates to productsInStore to drive the favorited UI state.
    if (this.location() === this.favoritesLocationEnum.FBT) {
      if (!this.productsInStore().get(skuOrEcode)) {
        return false;
      }
      return (
        !!this.productsInStore().get(skuOrEcode) || this.favoritedECodes().includes(skuOrEcode)
      );
    }

    return !!this.productsInStore().get(skuOrEcode);
  });

  readonly iconName = computed(() =>
    this.productIsFavorited() ? FavoritesIconEnum.FILLED : FavoritesIconEnum.OUTLINED,
  );

  protected readonly favoritesLocationEnum = FavoritesLocationEnum;

  private readonly beaconQueryParameters: BeaconQueryParameters = {
    placementType: 'INGRID',
    placementLevel: 'PRODUCT',
    beaconType: 'ADDTOWISHLIST',
  };

  constructor() {
    this.homefieldIconsLibraryService.registerIcons([
      homefieldIconReactionLoveFill,
      homefieldIconReactionLoveOutline,
    ]);
  }

  ngOnInit(): void {
    effect(
      () => {
        const savedProducts = this.savedProductsFromStore();
        if (this.location() !== this.favoritesLocationEnum.FAVORITES && savedProducts) {
          this.productsInStore.set(new Map(savedProducts));
        }
      },
      { injector: this.injector },
    );

    effect(
      () => {
        const actionState = this.favoritesActionState();

        if (!actionState) {
          return;
        }

        this.disabled.set(false);

        if (actionState.result === FavoritesActionResult.REMOVED) {
          if (actionState.productId) {
            this.deleteProductEmit.emit(actionState.productId);
          }
          this.snackbarService.showSnackBar({
            isAuthSnackbar: false,
            showSnackbar: true,
            text: `Removed from ${actionState.listName}`,
            link: '',
          });

          this.analyticsService.triggerAnalyticsEvent('RemoveFromList', {
            NumberOfLists: 1,
            ProductID: actionState.ecode,
            ProductSku: actionState.resolvedSku,
          });
          return;
        }

        if (actionState.result === FavoritesActionResult.REMOVAL_ERROR) {
          this.snackbarService.showSnackBar({
            isAuthSnackbar: false,
            showSnackbar: true,
            text: 'Unexpected error. Please try again later.',
            link:
              actionState.location === FavoritesLocationEnum.FAVORITES
                ? ''
                : '/MyAccount/Favorites',
          });
          this.analyticsService.singleErrorToAnalytics('RemoveFromList_Error');
        }
      },
      { injector: this.injector },
    );
  }

  iconClicked() {
    this.disabled.set(true);
    const openQuickView =
      this.location() === this.favoritesLocationEnum.PLP ||
      this.location() === this.favoritesLocationEnum.RECOMMENDED;

    const favoritesActionData: FavoritesActionDeterminationData = {
      isAuthenticated: this.isAuthenticated(),
      ecode: this.ecode(),
      sku: this.resolvedSku(),
      productIsFavorited: this.productIsFavorited(),
      favoritesLocation: this.location(),
      productsInStore: this.productsInStore(),
      favoritedProductId: this.product()?.id,
      openQuickView,
      quickViewOptions: {
        referrer: location.pathname,
        additionalData:
          this.location() === this.favoritesLocationEnum.RECOMMENDED
            ? {
                dsDisplayName: this.activatedRoute.snapshot.queryParams.dsDisplayName,
                dsDiversityLevel: '',
                dsPlacementId: this.activatedRoute.snapshot.queryParams.dsPlacementId,
                dsPosition: this.listIndex(),
                dsPriceRerankLevel: this.activatedRoute.snapshot.queryParams.dsPriceRerankLevel,
                dsRecsGCPProject: '',
              }
            : undefined,
      },
    };

    if (this.onWishlistBeacon()) {
      favoritesActionData.onWishlistBeacon = () =>
        this.dmnAdService.sendBeacon(this.onWishlistBeacon(), this.beaconQueryParameters);
    }

    this.favoritesActionRequestState.set(favoritesActionData);
  }
}
